import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

import { DEAL_SIGNAL_POLL_INTERVAL_MS } from "../../constants";
import type { SignalRecommenderHistory } from "../../types/model";
import { isDealSignalActivelyProcessing } from "../../utils/dealSignalStatus";
import { mapSignalRecommenderHistory } from "../../utils/mapSignalRecommenderHistory";
import { signalRecommenderQueryKeys } from "../key-factory/factory";

export const useSignalRecommenderFilesQuery = (): UseQueryResult<SignalRecommenderHistory> => {
    return useQuery({
        queryKey: signalRecommenderQueryKeys.files(),
        queryFn: ({ signal }) => salesApiService.signalRecommender.fetchFiles({ cancellation: signal }),

        // Module-level, so the reference is stable: the observer caches the mapped result against both the
        // cached payload and this function's identity, and an inline arrow would defeat half of that.
        select: mapSignalRecommenderHistory,
        retry: false,

        // The app defaults to a 15-minute staleTime with `refetchOnMount: false`, which for a job board
        // means re-entering the page can render quarter-hour-old rows until the first tick corrects them.
        staleTime: 0,
        refetchOnMount: true,

        // Polling belongs to the query, not to whoever started a job: an upload from an earlier session,
        // or a second upload in this one, is covered by the same predicate with no extra bookkeeping.
        // `query.state.data` is the raw `queryFn` payload — the DTO envelope, not the `select` output.
        //
        // A run past the processing window no longer counts as in progress here, which is what ends the
        // poll: the backend never reports failure for an abandoned job, so waiting on one is unbounded.
        refetchInterval: (query) =>
            query.state.data?.enrichments.some((file) => isDealSignalActivelyProcessing(file))
                ? DEAL_SIGNAL_POLL_INTERVAL_MS
                : false,

        // Once nothing is in progress the polling stops, and the rows then age silently for as long as the
        // tab stays open — a download clicked against a quarter-hour-old row can fail against a state that
        // has since moved. Returning to the tab is the cheapest moment to resynchronise.
        refetchOnWindowFocus: true,
    });
};
